'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { sidebarCategories } from '../widgets/registry/registry';
import { ScrollArea } from './ui/scroll-area';
import { cn } from '../lib/utils';

/**
 * The widget categories and their widgets. Strong Overline group eyebrows; the active item uses
 * the trading-app NavbarOption pattern -- green text on a subtle neutral row -- so it's clearly
 * selected and on-brand (no colored pill). Shared by the sidebar and the phone menu.
 */
export function WidgetCategoryLinks() {
    const pathname = usePathname();
    return (
        <nav aria-label="Widgets" className="flex flex-col gap-6">
            {sidebarCategories().map((cat) => (
                <div key={cat.slug} className="flex flex-col gap-1">
                    <div className="typ-overline-x10 mb-1 px-2.5 text-text-x10">{cat.title}</div>
                    <ul className="flex flex-col gap-0.5">
                        {cat.widgets.map((widget) => {
                            const href = `/widgets/${cat.slug}/${widget.meta.id}`;
                            // Exact match or a real sub-path only, so a sibling whose id is a
                            // prefix (e.g. `kpow` vs `kpow-broadcast`) doesn't also light up.
                            const active = pathname === href || pathname?.startsWith(`${href}/`);
                            return (
                                <li key={widget.meta.id}>
                                    <Link
                                        href={href}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn(
                                            'typ-emphasis-x30 block rounded-x30 px-2.5 py-1.5 font-kalshi-sans no-underline transition-colors',
                                            active
                                                ? 'bg-fill-x50 text-green-x10'
                                                : 'text-text-x20 hover:bg-fill-x55 hover:text-text-x10',
                                        )}
                                    >
                                        {widget.meta.title}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ))}
        </nav>
    );
}

/** Gallery nav, docked under the site header on wider screens. */
export function Sidebar({ collapsed = false, onToggle }: { collapsed?: boolean; onToggle?: () => void }) {
    if (collapsed) {
        return (
            <div className="sticky top-[72px] hidden h-[calc(100vh-72px)] w-12 flex-shrink-0 border-r border-stroke-x40 wsm:block">
                <button
                    onClick={onToggle}
                    className="flex h-full w-full items-start justify-center pt-6 text-text-x20 transition-colors hover:text-text-x10"
                    aria-label="Expand sidebar"
                >
                    <ChevronRight className="h-4 w-4" />
                </button>
            </div>
        );
    }

    return (
        <div className="sticky top-[72px] hidden h-[calc(100vh-72px)] w-60 flex-shrink-0 border-r border-stroke-x40 wsm:block">
            <div className="flex h-full flex-col px-3 pt-6">
                <button
                    onClick={onToggle}
                    className="typ-overline-x10 mb-6 ml-2.5 flex items-center gap-1.5 self-start text-text-x30 transition-colors hover:text-text-x10"
                    aria-label="Collapse sidebar"
                >
                    <ChevronLeft className="h-3 w-3" />
                    Collapse
                </button>
                <ScrollArea className="flex-1">
                    <WidgetCategoryLinks />
                </ScrollArea>
            </div>
        </div>
    );
}
